import {convertToSmallAvatar, addAvatar} from '@webex/redux-module-avatar';
import {subscribeToPresenceUpdates} from '@webex/redux-module-presence';
import {validateAndDecodeId} from '@webex/react-component-utils';

import {constructUser, constructCurrentUser} from './helpers';

export const STORE_USER = 'users/STORE_USER';
export const STORE_USERS = 'users/STORE_USERS';
export const FETCH_USER_REQUEST = 'users/FETCH_USER_REQUEST';
export const STORE_CURRENT_USER_ID = 'users/STORE_CURRENT_USER_ID';
export const STORE_CURRENT_USER = 'users/STORE_CURRENT_USER';
export const FETCH_CURRENT_USER_REQUEST = 'users/FETCH_CURRENT_USER_REQUEST';

export const PENDING_STATUS = 'PENDING';

function fetchUserRequest({email, id}) {
  return {
    type: FETCH_USER_REQUEST,
    payload: {
      email,
      id
    }
  };
}

export function storeUser(user) {
  return {
    type: STORE_USER,
    payload: {
      user: constructUser(user)
    }
  };
}

export function storeUsers(users) {
  return {
    type: STORE_USERS,
    payload: {
      users: users.map((u) => constructUser(u))
    }
  };
}

function fetchCurrentUserRequest(id) {
  return {
    type: FETCH_CURRENT_USER_REQUEST,
    payload: {
      id
    }
  };
}

function storeCurrentUser(user) {
  return {
    type: STORE_CURRENT_USER,
    payload: {
      user: constructCurrentUser(user)
    }
  };
}

function storeCurrentUserId(id) {
  return {
    type: STORE_CURRENT_USER_ID,
    payload: {
      id
    }
  };
}

/**
 * Retrieves the current user using internal APIs
 * @param {Object} sparkInstance
 * @param {bool} disablePresence
 * @returns {Function}
 */
export function fetchCurrentUser(sparkInstance, disablePresence) {
  return (dispatch, getState) => {
    const {users} = getState();
    // Check for stored current User
    let userId = users.get('currentUserId');

    if (!sparkInstance) {
      return Promise.reject(new Error('spark instance is required to fetch current user'));
    }

    // Get userId from device registration
    if (!userId) {
      if (sparkInstance.internal.device && sparkInstance.internal.device.userId) {
        ({userId} = sparkInstance.internal.device);
        dispatch(storeCurrentUserId(userId));
      }
      else {
        return Promise.reject(new Error('cannot retrieve current user. spark device is not registered.'));
      }
    }

    if (userId) {
      const currentUser = users.getIn(['byId', userId]);

      if (currentUser) {
        return Promise.resolve(currentUser);
      }
    }
    dispatch(fetchCurrentUserRequest(userId));
    // call subscription api conditionally
    if (!disablePresence) {
      dispatch(subscribeToPresenceUpdates([userId], sparkInstance));
    }

    return sparkInstance.internal.user.get()
      .then((user) => {
        dispatch(storeCurrentUser(user));
        if (user.photos && user.photos[0] && user.photos[0].url) {
          const {id: personId} = validateAndDecodeId(userId);

          dispatch(addAvatar(personId, convertToSmallAvatar(user.photos[0].url)));
        }

        return Promise.resolve(user);
      });
  };
}

/**
 * Performs an API call to fetch and store user details
 * @param {Object} user
 * @param {String} user.email
 * @param {String} user.id
 * @param {Object} sparkInstance
 * @returns {Function}
 */
export function fetchUser({email, id}, sparkInstance) {
  return (dispatch) => {
    if (!sparkInstance) {
      return Promise.reject(new Error('spark instance is required to fetch users'));
    }

    const handleResponse = (person) => {
      dispatch(storeUser(person));
      const {id: personId} = validateAndDecodeId(person.id);

      dispatch(addAvatar(personId, convertToSmallAvatar(person.avatar)));

      return Promise.resolve(person);
    };

    if (email) {
      return sparkInstance.people.list({email})
        .then((res) => res.items[0])
        .then(handleResponse);
    }

    if (id) {
      return sparkInstance.people.get(id)
        .then(handleResponse);
    }

    return Promise.reject(new Error('user email or id is required'));
  };
}

/**
 * Retrieves user from store or makes an API call if it doesn't exist
 * @param {Object} user
 * @param {String} user.email
 * @param {String} user.id
 * @param {Object} sparkInstance
 * @returns {Function}
 */
export function getUser({email, id}, sparkInstance) {
  return (dispatch, getState) => {
    // Check if we've fetched or already fetched this user
    const {users} = getState();
    const userId = validateAndDecodeId(id).id || users.getIn(['byEmail', email]);

    if (userId) {
      const user = users.getIn(['byId', userId]);

      // If we've already fetched this user
      if (user) {
        // If we haven't finished getting the user
        if (user.status.isFetching) {
          return Promise.resolve();
        }

        return Promise.resolve(user);
      }
    }

    // Initiate an API call to get user
    dispatch(fetchUserRequest({email, id: userId}));

    return dispatch(fetchUser({email, id: userId}, sparkInstance));
  };
}
